import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { useMainStore } from "@/store/MainStore";
import { AuthGuard } from "./authGuard";
import { ProductFruitsGuard } from "./productFruitsGuard";
import MainLayout from "@/Views/MainLayout.vue";
import NotFound from "@/Views/404.vue";
import LeadForm from "@/Partials/SelfSignUp/SelfSignUpForm.vue";
import PostSubmissionBanner from "@/Partials/SelfSignUp/PostSubmission.vue";
import FEATURE_FLAGS from "@/split-adapter/feature-flags";

const determineDestination = async () => {
  let destination;
  const mainStore = useMainStore();
  mainStore.setLoadingState(true);
  await mainStore.fetchAllStores();
  if (mainStore.allStoreIds.length) {
    destination = "Stores";
  } else {
    await mainStore.checkStoreSubscriptionStatus();
    destination = !mainStore.isSubscribed ? "Welcome" : "PostSubmissionBanner";
  }
  mainStore.setLoadingState(false);
  return destination;
};

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Home",
    component: MainLayout,
    meta: { requiresAuth: true },
    beforeEnter: async (to, from, next) => {
      next({ name: "Stores" });
    },
  },
  {
    path: "/stores",
    name: "Stores",
    meta: { requiresAuth: true },
    component: () => import("@/Components/Stores/StoreLayout.vue"),
    beforeEnter: async (to, from) => {
      const destination = await determineDestination();
      if (destination !== "Stores") return { name: destination };
    },
  },
  {
    path: "/payables",
    name: "Payables",
    component: () => import("@/Components/InternalAdminView/InternalAdminLayout.vue"),
    meta: { requiresAuth: true, featureFlag: FEATURE_FLAGS.INTERNAL_ADMIN },
  },
  {
    path: "/welcome",
    name: "Welcome",
    component: LeadForm,
    meta: { requiresAuth: true },
    beforeEnter: async (to, from) => {
      const destination = await determineDestination();
      if (destination !== "Welcome") return { name: destination };
    },
  },
  {
    path: "/welcome-back",
    name: "PostSubmissionBanner",
    component: PostSubmissionBanner,
    meta: { requiresAuth: true },
    beforeEnter: async (to, from) => {
      const destination = await determineDestination();
      if (destination !== "PostSubmissionBanner") return { name: destination };
    },
  },
  {
    path: "/store/:entityId",
    name: "Dashboard",
    meta: { requiresAuth: true },
    component: () => import("@/Components/Stores/ViewDashboard/DashboardLayout.vue"),
    redirect: { name: "Overview" },
    beforeEnter: async (to, from, next) => {
      const mainStore = useMainStore();
      await mainStore.fetchAllStores();
      const isAccessible = mainStore.getAllStores.find((store) => store.entityId.toString() === to.params.entityId);
      if (isAccessible) {
        if (mainStore.selectedStore.entityId !== isAccessible.entityId) {
          mainStore.fetchSelectedStore(isAccessible.entityId);
        }
        next();
      } else {
        next({ name: "Stores" });
      }
    },
    children: [
      {
        path: "overview",
        name: "Overview",
        component: () => import("@/Components/Stores/ViewDashboard/Tabs/Overview.vue"),
      },
      {
        path: "orders",
        name: "Orders",
        component: () => import("@/Components/Stores/ViewDashboard/Tabs/Orders.vue"),
      },
      {
        path: "ads",
        name: "AdsDashboard",
        component: () => import("@/Components/Stores/ViewDashboard/Tabs/Ads.vue"),
        children: [
          {
            path: "",
            name: "Ads",
            component: () => import("@/Partials/Ads/AdsOrderDetails.vue"),
            meta: { featureFlag: FEATURE_FLAGS.SUPPORT_ADS },
          },
          {
            path: "products",
            name: "AdsProducts",
            component: () => import("@/Partials/Ads/AdsProductDetails.vue"),
            meta: { featureFlag: FEATURE_FLAGS.SUPPORT_ADS },
          },
          {
            path: "withdrawals",
            name: "FundsWithdrawalRequests",
            component: () => import("@/Partials/Ads/FundsWithdrawalRequests.vue"),
            meta: { featureFlag: FEATURE_FLAGS.SUPPORT_ADS },
          },
        ],
        meta: { featureFlag: FEATURE_FLAGS.SUPPORT_ADS },
      },
      {
        path: "points",
        name: "Points",
        component: () => import("@/Components/Stores/ViewDashboard/Tabs/Points.vue"),
      },
      {
        path: "support",
        name: "Support",
        component: () => import("@/Components/Stores/ViewDashboard/Tabs/Support.vue"),
      },
      {
        path: "edit",
        name: "Edit",
        component: () => import("@/Components/Stores/ViewDashboard/Tabs/Edit.vue"),
      },
    ],
  },
  {
    path: "/:catchAll(.*)",
    component: NotFound,
  },
];
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return { top: 0 };
  },
});

router.beforeEach(AuthGuard);
router.beforeEach(ProductFruitsGuard);

export default router;
